import Grid from '@mui/material/Grid';
import { PageSection } from '@skoda/k2tools-app-components';
import * as React from 'react';
import Swipe from 'react-easy-swipe';
import { StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { OwnersManualsAppContext } from '../../contexts/OwnersManualsContext';
import { CarModel } from '../../models';
import { escapeOutput } from '../../utils/helpers';
import { ManualSearchDialog } from '../manual-search-dialog';
import styles from './styles.scss';

type RouteState = {
	isCurrentModel?: boolean;
	isScrolledToFamily?: boolean;
};

type FamilyListProps = RouteComponentProps<{}, StaticContext, RouteState> & {
	onModelToggle?: () => void;
};

type FamilyListState = {
	isCurrentModels: boolean;
	isScrolled: boolean;
};

class FamilyList extends React.Component<FamilyListProps, FamilyListState> {
	static contextType = OwnersManualsAppContext;
	context: React.ContextType<typeof OwnersManualsAppContext>;

	private familyListRef: React.RefObject<HTMLDivElement>;
	private swipeStartScreenX: number;

	constructor(props: FamilyListProps) {
		super(props);

		this.state = {
			isCurrentModels: this.props.location.state?.isCurrentModel !== false,
			isScrolled: this.props.location.state?.isScrolledToFamily ?? false,
		};

		this.familyListRef = React.createRef<HTMLDivElement>();
	}

	componentDidMount() {
		if (this.state.isScrolled) {
			window.scrollTo(0, this.familyListRef.current.offsetTop);
		}
	}

	handleTabChange(val: boolean) {
		this.setState({ isCurrentModels: val });
		this.context.services.trackingPrepare.trackTabChange(val);
	}

	handleModelSelect(model: CarModel) {
		this.context.setModel(model);
		this.props.onModelToggle();
		this.context.services.trackingPrepare.trackFamilyModelClick(this.state.isCurrentModels, model.modelType);
	}

	handleModelUnselect() {
		this.context.setModel(null);
	}

	getCarModelElement(model: CarModel) {
		const title = escapeOutput(this.context.services.resourceProvider.getText(model.titleResourceKey));

		return (
			<Grid key={`car-${model.modelType}`} item xl={3} lg={3} md={4} sm={6} xs={12}>
				<div
					data-test='car-model'
					id={'car-model-' + model.modelType}
					className={`${styles['list-item']}`}
					onClick={() => this.handleModelSelect(model)}
				>
					{model.isNew && (
						<p className={styles['list-item-new']}>{this.context.services.resourceProvider.getText(`LabelNew_${model.labelNewVariant}`)}</p>
					)}
					<h3 dangerouslySetInnerHTML={{ __html: this.customizeModelTitle(title) }} />
					<img className={styles['image']} src={model.imagePath} alt='' />
				</div>
			</Grid>
		);
	}

	customizeModelTitle(modelTitle: string) {
		if (!modelTitle || modelTitle.length < 12 || modelTitle.substr(0, 12).toUpperCase() !== 'OCTAVIA TOUR') {
			return modelTitle;
		}

		return `${modelTitle.substr(0, 7)}<br>${modelTitle.substr(8)}`;
	}

	onSwipeStart = (event: React.TouchEvent) => {
		if (event.changedTouches && event.changedTouches.length > 0) {
			this.swipeStartScreenX = event.changedTouches[0].screenX;
		}
	};

	onSwipeEnd = (event: React.TouchEvent) => {
		if (event.changedTouches && event.changedTouches.length > 0 && this.swipeStartScreenX > 0) {
			const swipeEndScreenX = event.changedTouches[0].screenX;
			const swipeDiff = swipeEndScreenX - this.swipeStartScreenX;
			if (swipeDiff < -30 && this.state.isCurrentModels) {
				this.handleTabChange(false);
			} else if (swipeDiff > 30 && !this.state.isCurrentModels) {
				this.handleTabChange(true);
			}
		}
		this.swipeStartScreenX = 0;
	};

	render() {
		return (
			<PageSection>
				<div className={styles['area-family-list']} ref={this.familyListRef}>
					<h2 className={`${styles['heading']}`}>{this.context.services.resourceProvider.getText('Show_model')}</h2>
					<div className={`overflow-hidden`}>
						<Swipe onSwipeStart={this.onSwipeStart} onSwipeEnd={this.onSwipeEnd}>
							<div className={`${styles['container-tabs']}`}>
								<table className={!this.state.isCurrentModels ? styles['table-switched'] : ''}>
									<thead></thead>
									<tbody>
										<tr>
											<td
												id='btn-current-models'
												onClick={() => this.handleTabChange(true)}
												className={this.state.isCurrentModels ? styles['tab-active'] : ''}
											>
												{this.context.services.resourceProvider.getText('Current_Models_Without_Skoda')}
											</td>
											<td></td>
											<td
												id='btn-old-models'
												onClick={() => this.handleTabChange(false)}
												className={!this.state.isCurrentModels ? styles['tab-active'] : ''}
											>
												{this.context.services.resourceProvider.getText('Older_models_Without_Skoda')}
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</Swipe>
					</div>
					<Grid container spacing={5}>
						{this.context.carModels.length > 0 &&
							this.state.isCurrentModels &&
							this.context.carModels.filter((model) => !model.isOlder).map((model) => this.getCarModelElement(model))}
						{this.context.carModels.length > 0 &&
							!this.state.isCurrentModels &&
							this.context.carModels.filter((model) => model.isOlder).map((model) => this.getCarModelElement(model))}
					</Grid>
				</div>
				{this.context.model && !this.context.hasError && (
					<ManualSearchDialog
						carModel={this.context.model}
						title={this.context.model.titleResourceKey}
						image={this.context.model.imagePath}
						hasDigitalManuals={this.context.model.hasDigitalManuals}
						handleClose={() => this.handleModelUnselect()}
						isCurrentModel={!this.context.model.isOlder}
					/>
				)}
				{this.state.isCurrentModels && (
					<div className={styles['area-cert-links']}>
						<div className={styles['link']}>
							<a href='https://www.skoda-auto.com/services/red-doc' target='_blank' rel='noopener noreferrer'>
								<div className={styles['text']}>{this.context.services.resourceProvider.getText('Link_Cert_Text')}</div>
							</a>
							<a href='https://www.skoda-auto.com/services/red-doc' target='_blank' rel='noopener noreferrer'>
								<ReactSVG src={this.context.services.config.Icons.Arrow} className={`${styles['icon-arrow']}`} />
							</a>
						</div>
					</div>
				)}
			</PageSection>
		);
	}
}

export default FamilyList;
